import { useId, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import { ChartTooltip } from "./Tooltip";
import { compact, niceTicks, seriesColor, shortTime, toMs } from "./util";
import { useWidth } from "./useWidth";

export interface LinePoint {
  t: string | number;
  v: number | null | undefined;
}
export interface LineSeries {
  name: string;
  points: LinePoint[];
  /** Optional explicit color; defaults to the categorical slot by index. */
  color?: string;
}

export interface LineChartProps {
  series: LineSeries[];
  height?: number;
  ariaLabel: string;
  formatValue?: (v: number) => string;
  /** Show a 10% area wash under the first series. */
  area?: boolean;
  /** Fixed y max (e.g. 100 for percentages). */
  yMax?: number;
  /** Hide the table view toggle (when the parent renders its own table). */
  noTable?: boolean;
}

const PAD = { l: 44, r: 16, t: 10, b: 24 };

/**
 * Multi-series line chart. One y axis (never two), 2px lines with round
 * joins, ≥8px end markers with a 2px surface ring, hairline gridlines, a
 * crosshair that snaps to the nearest x, a single tooltip listing every
 * series, a legend for ≥2 series, and a table view.
 */
export function LineChart({ series, height = 180, ariaLabel, formatValue = compact, area, yMax, noTable }: LineChartProps) {
  const id = useId();
  const ref = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<number | null>(null);
  const [focused, setFocused] = useState(false);
  const width = useWidth(ref, 640);

  const xs = useMemo(() => {
    const set = new Map<string, number>();
    for (const s of series) for (const p of s.points) set.set(String(p.t), toMs(p.t));
    return Array.from(set.entries()).sort((a, b) => a[1] - b[1]);
  }, [series]);
  const n = xs.length;
  const values = useMemo(() => series.map((s) => new Map(s.points.map((p) => [String(p.t), p.v]))), [series]);
  const maxV = useMemo(() => {
    let m = 0;
    for (const s of series) for (const p of s.points) if (typeof p.v === "number" && p.v > m) m = p.v;
    return yMax ?? m;
  }, [series, yMax]);
  const ticks = useMemo(() => niceTicks(maxV, 4), [maxV]);
  const top = ticks[ticks.length - 1] || 1;
  const plotW = width - PAD.l - PAD.r;
  const plotH = height - PAD.t - PAD.b;
  const xAt = (i: number) => PAD.l + (n <= 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const yAt = (v: number) => PAD.t + plotH - (v / top) * plotH;
  const spanMs = n > 1 ? xs[n - 1]![1] - xs[0]![1] : 0;

  const paths = series.map((s, si) => {
    let d = "";
    let started = false;
    const pts: Array<{ x: number; y: number; i: number }> = [];
    xs.forEach(([k], i) => {
      const v = values[si]!.get(k);
      if (typeof v !== "number") {
        started = false;
        return;
      }
      const x = xAt(i);
      const y = yAt(v);
      pts.push({ x, y, i });
      d += `${started ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`;
      started = true;
    });
    return { d, pts, color: s.color ?? seriesColor(si) };
  });

  const hasData = n > 0 && paths.some((p) => p.pts.length > 0);
  const idx = hover ?? (focused ? n - 1 : null);

  const onMove = (e: PointerEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * width;
    if (n <= 1) return setHover(n ? 0 : null);
    const i = Math.round(((px - PAD.l) / plotW) * (n - 1));
    setHover(Math.max(0, Math.min(n - 1, i)));
  };
  const onKey = (e: KeyboardEvent<SVGSVGElement>) => {
    if (!n) return;
    if (e.key === "ArrowRight") setHover((h) => Math.min(n - 1, (h ?? n - 1) + 1));
    else if (e.key === "ArrowLeft") setHover((h) => Math.max(0, (h ?? n - 1) - 1));
    else if (e.key === "Home") setHover(0);
    else if (e.key === "End") setHover(n - 1);
    else return;
    e.preventDefault();
  };

  const tipRows =
    idx !== null && n
      ? series.map((s, si) => ({ name: s.name, value: (() => { const v = values[si]!.get(xs[idx]![0]); return typeof v === "number" ? formatValue(v) : "—"; })(), color: s.color ?? seriesColor(si) }))
      : [];
  const containerW = width;
  const tipX = idx !== null && n ? xAt(idx) : 0;

  return (
    <div className="viz viz-chart" ref={ref}>
      {series.length >= 2 && (
        <div className="viz-legend" aria-hidden="true">
          {series.map((s, i) => (
            <span className="key" key={s.name}>
              <i style={{ background: s.color ?? seriesColor(i) }} /> {s.name}
            </span>
          ))}
        </div>
      )}
      {!hasData ? (
        <div className="viz-empty" role="img" aria-label={`${ariaLabel}: no data`}>
          No data in this range
        </div>
      ) : (
        <svg
          viewBox={`0 0 ${width} ${height}`}
          width={width}
          height={height}
          role="img"
          aria-label={ariaLabel}
          aria-describedby={`${id}-desc`}
          tabIndex={0}
          onPointerMove={onMove}
          onPointerLeave={() => setHover(null)}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            setHover(null);
          }}
          onKeyDown={onKey}
        >
          <desc id={`${id}-desc`}>
            {series.map((s) => `${s.name}: ${s.points.length} points, max ${formatValue(Math.max(0, ...s.points.map((p) => (typeof p.v === "number" ? p.v : 0))))}`).join("; ")}
          </desc>
          {ticks.map((tk) => (
            <g key={tk}>
              <line className={tk === 0 ? "viz-axis" : "viz-grid"} x1={PAD.l} x2={width - PAD.r} y1={yAt(tk)} y2={yAt(tk)} />
              <text className="viz-tick" x={PAD.l - 6} y={yAt(tk) + 3} textAnchor="end">
                {formatValue(tk)}
              </text>
            </g>
          ))}
          {n > 0 &&
            [0, Math.floor((n - 1) / 2), n - 1].filter((v, i, a) => a.indexOf(v) === i).map((i) => (
              <text key={i} className="viz-tick" x={xAt(i)} y={height - 6} textAnchor={i === 0 ? "start" : i === n - 1 ? "end" : "middle"}>
                {shortTime(xs[i]![0], spanMs)}
              </text>
            ))}
          {area && paths[0] && paths[0].pts.length > 1 && (
            <path className="viz-area" fill={paths[0].color} d={`${paths[0].d} L${paths[0].pts[paths[0].pts.length - 1]!.x},${yAt(0)} L${paths[0].pts[0]!.x},${yAt(0)} Z`} />
          )}
          {paths.map((p, i) => (
            <g key={i}>
              <path className="viz-line" stroke={p.color} d={p.d} />
              {p.pts.length > 0 && <circle className="viz-marker" fill={p.color} r={4} cx={p.pts[p.pts.length - 1]!.x} cy={p.pts[p.pts.length - 1]!.y} />}
              {p.pts.length === 1 && <circle className="viz-marker" fill={p.color} r={4} cx={p.pts[0]!.x} cy={p.pts[0]!.y} />}
            </g>
          ))}
          {idx !== null && (
            <g>
              <line className="viz-crosshair" x1={xAt(idx)} x2={xAt(idx)} y1={PAD.t} y2={PAD.t + plotH} />
              {paths.map((p, i) => {
                const pt = p.pts.find((q) => q.i === idx);
                return pt ? <circle key={i} className="viz-marker" fill={p.color} r={4} cx={pt.x} cy={pt.y} /> : null;
              })}
            </g>
          )}
        </svg>
      )}
      {idx !== null && hasData && <ChartTooltip x={tipX} y={PAD.t} width={containerW} head={new Date(xs[idx]![1]).toLocaleString()} rows={tipRows} />}
      {!noTable && hasData && (
        <details className="viz-table-toggle">
          <summary>Table view</summary>
          <table>
            <thead>
              <tr>
                <th>Time</th>
                {series.map((s) => (
                  <th key={s.name}>{s.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {xs.map(([k, ms]) => (
                <tr key={k}>
                  <td>{new Date(ms).toLocaleString()}</td>
                  {series.map((s, si) => {
                    const v = values[si]!.get(k);
                    return <td key={s.name}>{typeof v === "number" ? formatValue(v) : "—"}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </div>
  );
}
